import React from 'react';
import {
	Input,
	Popover,
	Tooltip,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import {
	HIIFieldTypes,
	DATE_AND_TIME,
	Currencies,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { stringToValidNumericString } from '@shared/utils/string';
import type HireInvoiceItem from '@shared/hireInvoice/HireInvoiceItem';
import { formatDate } from '@client/utils/formatDate';
import Select from '@client/components/Select';
import Button from '@client/components/Button';
import DatePicker, { DatePickerValue } from '@client/components/DatePicker';
import CurrencyInput from '@client/components/CurrencyInput';
import NumericInput from '@client/components/NumericInput';
import styles from './styles/EditReceivedPopover.module.css';

type FieldValue = number | boolean | string | null;

type FieldInputProps = {
	type: Values<typeof HIIFieldTypes>;
	options?: Array<{ label: string; value: string }>;
	placeholderValue?: FieldValue;
	currency?: Values<typeof Currencies>;
	baseCurrency?: Values<typeof Currencies>;
	value: FieldValue;
	disabled: boolean;
	className?: string;
	onChange: (newValue: FieldValue) => void;
};

const FieldInput = ({
	type,
	options,
	placeholderValue,
	currency,
	baseCurrency,
	value,
	onChange,
	...props
}: FieldInputProps) => {
	const placeholder = placeholderValue == null ? undefined : String(placeholderValue);

	switch (type) {
		case HIIFieldTypes.NUMBER:
			return (
				<NumericInput
					placeholder={placeholder}
					{...props}
					value={value as number | null}
					onChange={onChange}
				/>
			);
		case HIIFieldTypes.CURRENCY:
			return (
				<CurrencyInput
					currency={currency}
					placeholder={stringToValidNumericString(
						placeholderValue == null ? '' : placeholderValue.toString(),
						{ separateThousands: true },
					) ?? undefined}
					{...props}
					value={value as number | null}
					onChange={onChange}
				/>
			);

		case HIIFieldTypes.EXCHANGE_RATE:
			return (
				<NumericInput
					placeholder="Exchange Rate"
					addonAfter={`${currency} = 1 ${baseCurrency}`}
					{...props}
					value={placeholderValue as number | null}
					onChange={onChange}
				/>
			);
		case HIIFieldTypes.TEXT:
			return (
				<Input
					placeholder={placeholder}
					{...props}
					value={value == null ? undefined : String(value)}
					onChange={(e) => onChange(e.target.value)}
				/>
			);
		case HIIFieldTypes.PERCENTAGE:
			return (
				<NumericInput
					{...props}
					placeholder={placeholder}
					addonAfter="%"
					value={value as number | null}
					onChange={onChange}
				/>
			);
		case HIIFieldTypes.SELECT:
			return (
				<Select
					{...props}
					className={styles.select}
					value={value as string | number | boolean | null}
					onChange={onChange}
					options={(options ?? []).map((o) => ({
						label: o.label,
						value: o.value,
					}))}
				/>
			);
		case HIIFieldTypes.DATE:
			return (
				<DatePicker
					placeholder={formatDate(placeholderValue as string | null, DATE_AND_TIME) ?? undefined}
					{...props}
					value={value as unknown as DatePickerValue}
					onChange={(newValue) => onChange(newValue as unknown as FieldValue)}
					time
					allowClear={false}
				/>
			);

		default:
			return null;
	}
};

type EditReceivedPopoverProps = {
	item: HireInvoiceItem;
	changedItem: HireInvoiceItem;
	disabled: boolean;
	disabledFields?: string[];
	onEditOurValue: (fieldName: string, newValue: FieldValue) => void;
	ourTotal: number;
	ourName: string;
	fixtureCurrency: Values<typeof Currencies>;
};

const EditReceivedPopover = ({
	item,
	changedItem,
	disabled,
	disabledFields = [],
	onEditOurValue,
	ourTotal,
	ourName,
	fixtureCurrency,
}: EditReceivedPopoverProps) => {
	const ourItem = changedItem;

	const getValue = (
		matchingItem: HireInvoiceItem | null,
		fieldName: string,
		totalValue: number,
	): FieldValue => {
		if (matchingItem == null) {
			return 0;
		}

		const itemValues = matchingItem as unknown as Record<string, FieldValue>;

		if (fieldName === 'totalOverride' && itemValues.totalOverride == null) {
			return totalValue;
		}

		if (itemValues[fieldName] == null) {
			return 0;
		}

		return itemValues[fieldName];
	};

	const getItemCurrency = (fieldName: string) => {
		if (fieldName === 'totalOverride') {
			return fixtureCurrency;
		}

		return item.currency || fixtureCurrency;
	};

	return (
		<Popover
			trigger={disabled ? [] : ['click']}
			placement="right"
			content={(
				<div className={styles.editValuesContainer}>
					<div>
						<h2>{ourName}</h2>
					</div>
					{Object.entries(item.getFields())
						.map(([fieldName, properties]) => {
							const ourValue = getValue(ourItem, fieldName, ourTotal);
							const { inputProps } = properties;
							const fieldDisabled = disabledFields.includes(fieldName);

							return (
								<React.Fragment key={fieldName}>
									<div>
										<div>
											<strong>{properties.label}</strong>
											<FieldInput
												{...properties}
												{...inputProps}
												className={styles.inputField}
												type={properties.type}
												value={ourValue}
												disabled={fieldDisabled}
												onChange={(newValue) => {
													if (fieldDisabled) {
														return;
													}

													onEditOurValue(fieldName, newValue);
												}}
												currency={getItemCurrency(fieldName)}
											/>
										</div>
									</div>
								</React.Fragment>
							);
						})}
					<br />
				</div>

			)}
		>
			<Tooltip
				trigger={disabled ? ['hover'] : []}
			>
				<Button
					type="link"
					icon={(<EditOutlined />)}
					disabled={disabled}
				/>
			</Tooltip>
		</Popover>
	);
};

export default EditReceivedPopover;
